import { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  TextArea,
  Control,
  Label,
  SegmentedControl,
  SegmentedControlButton,
  Chip,
  InfoIcon,
  SearchIcon,
  SendIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { keycloakApi } from '@/api/keycloak';
import type { UserType } from '@/api/keycloak';
import { toTitleCase } from '@/utils/userDisplay';

interface InviteUserDialogProps {
  availableGroups: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSuccess: () => void;
}

interface InviteData {
  type: 'internal' | 'external';
  email: string;
  message?: string;
  groups: string[];
}

const InviteUserDialog = ({
  availableGroups,
  onClose,
  onSuccess,
}: InviteUserDialogProps) => {
  const [userType, setUserType] = useState<'internal' | 'external'>('internal');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [groupSearch, setGroupSearch] = useState('');

  const filteredGroups = availableGroups.filter(
    (g) => !groupSearch || g.name.toLowerCase().includes(groupSearch.toLowerCase())
  );

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((g) => g !== groupId)
        : [...prev, groupId]
    );
  };

  const { mutate: inviteUser, isSubmitting } = useApiMutation<InviteData, void>({
    mutationFn: async (data) => {
      if (data.type === 'external') {
        // External users are invited as Azure AD B2B guests via Microsoft Graph.
        // They only exist as a portal (Keycloak) user after they redeem the
        // invitation and sign in, so groups can't be pre-assigned here.
        await keycloakApi.inviteExternalUser({
          email: data.email,
          message: data.message,
        });
        return;
      }

      // Internal Honeywell users authenticate through Azure AD and are
      // auto-provisioned on first sign-in. Pre-create their portal record so
      // group memberships can be assigned ahead of that first login.
      const localPart = data.email.split('@')[0];
      const createdUser = await keycloakApi.createUser({
        username: data.email,
        email: data.email,
        firstName: toTitleCase(localPart.split('.')[0]) || 'New',
        lastName: toTitleCase(localPart.split('.')[1]) || 'User',
        userType: 'internal' as UserType,
      });

      for (const groupId of data.groups) {
        await keycloakApi.addUserToGroup(groupId, createdUser.id);
      }
    },
    successMessage:
      userType === 'external'
        ? 'Azure AD B2B guest invitation sent'
        : 'Internal user provisioned',
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = async () => {
    if (!email) return;
    await inviteUser({
      type: userType,
      email,
      message: userType === 'external' ? message : undefined,
      groups: userType === 'internal' ? selectedGroups : [],
    });
  };

  const isValid = !!email;

  const modeHint =
    userType === 'internal'
      ? 'Honeywell users sign in through Azure AD and are provisioned automatically on first login. This pre-creates their portal record so you can assign group memberships now.'
      : 'A real Azure AD B2B guest invitation is emailed via Microsoft Graph. The user redeems it in Azure AD, then lands on the OneRing portal. If Honeywell cross-tenant collaboration blocks the external tenant, the invitation fails and the reason is shown here.';

  return (
    <Modal
      title="Invite User"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<SendIcon />}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send Invitation'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-5">
          {/* User type selector + consistent contextual hint */}
          <div className="flex flex-col gap-2">
            <SegmentedControl
              id="invite-user-type"
              onChange={(e) => {
                setUserType(e.target.value as 'internal' | 'external');
                setEmail('');
                setSelectedGroups([]);
              }}
            >
              <SegmentedControlButton id="seg-internal" value="internal" defaultChecked>
                Internal User
              </SegmentedControlButton>
              <SegmentedControlButton id="seg-external" value="external">
                External User
              </SegmentedControlButton>
            </SegmentedControl>
            <div className="flex items-start gap-2 text-xs text-color-tertiary">
              <InfoIcon className="mt-0.5 shrink-0" />
              <span>{modeHint}</span>
            </div>
          </div>

          {/* Email field (shared) + optional external message */}
          <div className="flex flex-col gap-3">
            <Control hideSpacing="bottom">
              <Label htmlFor="invite-email">
                {userType === 'internal'
                  ? 'Honeywell Email Address'
                  : 'Email Address'}
              </Label>
              <Input
                id="invite-email"
                type="email"
                placeholder={
                  userType === 'internal'
                    ? 'user@honeywell.com'
                    : 'user@external-company.com'
                }
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Control>

            {userType === 'external' && (
              <Control hideSpacing="bottom">
                <Label htmlFor="ext-message">Personal Message (Optional)</Label>
                <TextArea
                  id="ext-message"
                  rows={3}
                  placeholder="Add a personal message to the invitation email..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Control>
            )}
          </div>

          {/* Groups — only assignable for internal users, who get a portal
              record immediately. External guests must sign in first. */}
          {userType === 'internal' ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="group-search">Groups (Optional)</Label>
              <Input
                id="group-search"
                placeholder="Search groups..."
                prefix={<SearchIcon />}
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
              />
              <div className="mt-1 flex max-h-32 flex-wrap gap-2 overflow-y-auto">
                {filteredGroups.map((group) => (
                  <Chip
                    key={group.id}
                    selectable
                    selected={selectedGroups.includes(group.id)}
                    onClick={() => handleGroupToggle(group.id)}
                  >
                    {group.name}
                  </Chip>
                ))}
              </div>
              {selectedGroups.length > 0 && (
                <p className="text-xs text-color-tertiary">
                  {selectedGroups.length} group
                  {selectedGroups.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-color-tertiary">
              Group memberships can be assigned from Access Management once the
              guest redeems the invitation and signs in for the first time.
            </p>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default InviteUserDialog;
